import {
  ChangeEvent,
  ClipboardEvent,
  DragEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useAppContext } from "../../../AppContext";
import { isAxiosError } from "../../../common/infrastructure/helpers/axiosHelper";
import { getErrorToast } from "../../../common/infrastructure/utilities/toastUtility";
import {
  AI_CHAR_COUNTER_THRESHOLD,
  IAiComposerAttachment,
  IAiMessageImage,
  aiAssistantTexts,
} from "../models/aiAssistantModels";
import { readErrorBody } from "../utilities/aiAssistantDataUtility";
import {
  AiImageError,
  isAcceptedImageFile,
  prepareAiImage,
  uploadAiImage,
} from "../utilities/aiImageUtility";

/** The textarea grows with its text up to this height, then scrolls. */
const MAX_TEXTAREA_HEIGHT_PX = 180;

interface IUseAiComposerArgs {
  maxChars: number;
  maxImages: number;
  maxImageBytes: number;
  /** A message is being answered: typing goes on, sending waits. */
  isSending: boolean;
  /** The budget is used up or the conversation is full. */
  disabled: boolean;
  /** Resolves false when the API refused the message before answering; the composer restores it. */
  onSubmit: (text: string, images: IAiMessageImage[]) => Promise<boolean>;
}

const hasDraggedFiles = (event: DragEvent) =>
  Array.from(event.dataTransfer?.types ?? []).includes("Files");

const uploadErrorMessage = (error: unknown, fileName: string): string => {
  if (error instanceof AiImageError) {
    return error.reason === "tooBig"
      ? aiAssistantTexts.imageTooBig(fileName)
      : aiAssistantTexts.imageUnreadable(fileName);
  }
  if (isAxiosError(error)) {
    const { message } = readErrorBody(error.response?.data);
    if (message) return message;
  }
  return aiAssistantTexts.imageUploadFailed(fileName);
};

/**
 * The message box under the chat: auto-growing text, Enter to send (Shift+Enter for a new line),
 * images from the attach button, the clipboard or a drop onto the chat, each converted, scaled
 * down and uploaded right away so sending is instant.
 */
export const useAiComposer = ({
  maxChars,
  maxImages,
  maxImageBytes,
  isSending,
  disabled,
  onSubmit,
}: IUseAiComposerArgs) => {
  const { currentUser } = useAppContext();
  const [text, setTextState] = useState("");
  const [attachments, setAttachmentsState] = useState<IAiComposerAttachment[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  const textRef = useRef("");
  const attachmentsRef = useRef<IAiComposerAttachment[]>([]);
  const objectUrlsRef = useRef<Set<string>>(new Set());
  const dragDepthRef = useRef(0);
  const localIdRef = useRef(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const setText = useCallback((value: string) => {
    textRef.current = value;
    setTextState(value);
  }, []);

  const setAttachments = useCallback((update: (current: IAiComposerAttachment[]) => IAiComposerAttachment[]) => {
    attachmentsRef.current = update(attachmentsRef.current);
    setAttachmentsState(attachmentsRef.current);
  }, []);

  const updateAttachment = useCallback(
    (localId: string, patch: Partial<IAiComposerAttachment>) => {
      setAttachments((current) =>
        current.map((attachment) => (attachment.localId === localId ? { ...attachment, ...patch } : attachment)),
      );
    },
    [setAttachments],
  );

  const isStillAttached = (localId: string) =>
    attachmentsRef.current.some((attachment) => attachment.localId === localId);

  const revoke = (url: string) => {
    if (objectUrlsRef.current.delete(url)) URL.revokeObjectURL(url);
  };

  // Previews also back the sent message's bubble until the stored copy arrives, so they live
  // as long as the page and are released when it goes.
  useEffect(
    () => () => {
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      objectUrlsRef.current.clear();
    },
    [],
  );

  const processFile = useCallback(
    async (file: File, localId: string) => {
      try {
        const prepared = await prepareAiImage(file, maxImageBytes);
        if (!isStillAttached(localId)) return;

        const previewUrl = URL.createObjectURL(prepared.blob);
        objectUrlsRef.current.add(previewUrl);
        updateAttachment(localId, { status: "uploading", previewUrl, fileName: prepared.fileName });

        const uploaded = await uploadAiImage(prepared, currentUser?.authToken, (progress) => {
          if (isStillAttached(localId)) updateAttachment(localId, { progress });
        });
        if (!isStillAttached(localId)) return;
        updateAttachment(localId, { status: "uploaded", progress: 1, uploaded });
      } catch (error) {
        if (!isStillAttached(localId)) return;
        getErrorToast(uploadErrorMessage(error, file.name));
        const failed = attachmentsRef.current.find((attachment) => attachment.localId === localId);
        if (failed?.previewUrl) revoke(failed.previewUrl);
        setAttachments((current) => current.filter((attachment) => attachment.localId !== localId));
      }
    },
    [currentUser?.authToken, maxImageBytes, setAttachments, updateAttachment],
  );

  const addFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0 || disabled) return;

      const images = files.filter(isAcceptedImageFile);
      if (images.length < files.length) getErrorToast(aiAssistantTexts.notAnImage);
      if (images.length === 0) return;

      const room = maxImages - attachmentsRef.current.length;
      if (images.length > room) getErrorToast(aiAssistantTexts.tooManyImages(maxImages));
      const accepted = images.slice(0, Math.max(0, room));

      const added: IAiComposerAttachment[] = accepted.map((file) => {
        localIdRef.current += 1;
        return {
          localId: `image-${localIdRef.current}`,
          fileName: file.name,
          previewUrl: "",
          status: "preparing",
          progress: 0,
          uploaded: null,
        };
      });
      if (added.length === 0) return;

      setAttachments((current) => [...current, ...added]);
      added.forEach((attachment, index) => void processFile(accepted[index], attachment.localId));
    },
    [disabled, maxImages, processFile, setAttachments],
  );

  const removeAttachment = useCallback(
    (localId: string) => {
      const attachment = attachmentsRef.current.find((item) => item.localId === localId);
      if (attachment?.previewUrl) revoke(attachment.previewUrl);
      setAttachments((current) => current.filter((item) => item.localId !== localId));
    },
    [setAttachments],
  );

  const trimmedText = text.trim();
  const isUploading = attachments.some((attachment) => attachment.status !== "uploaded");
  const uploadedCount = attachments.filter((attachment) => attachment.status === "uploaded").length;
  const isOverLimit = text.length > maxChars;
  const canSend =
    !disabled && !isSending && !isUploading && !isOverLimit && (trimmedText.length > 0 || uploadedCount > 0);

  const submit = useCallback(async () => {
    if (!canSend) return;

    const sentText = textRef.current;
    const sentAttachments = attachmentsRef.current;
    const images: IAiMessageImage[] = sentAttachments
      .filter((attachment) => attachment.uploaded)
      .map((attachment) => ({
        id: attachment.uploaded!.id,
        url: attachment.previewUrl || attachment.uploaded!.url,
      }));

    setText("");
    setAttachments(() => []);

    const accepted = await onSubmit(sentText.trim(), images);

    // Refused before answering (budget, busy, too fast): give the message back, unless the user
    // has started a new one meanwhile.
    if (!accepted && textRef.current === "" && attachmentsRef.current.length === 0) {
      setText(sentText);
      setAttachments(() => sentAttachments);
    }
  }, [canSend, onSubmit, setAttachments, setText]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
      event.preventDefault();
      void submit();
    },
    [submit],
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => setText(event.target.value),
    [setText],
  );

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLTextAreaElement>) => {
      const files = Array.from(event.clipboardData?.files ?? []).filter(isAcceptedImageFile);
      if (files.length === 0) return;
      // Only an image paste is taken over; pasted text goes into the box as usual.
      event.preventDefault();
      addFiles(files);
    },
    [addFiles],
  );

  const handleFileInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      addFiles(Array.from(event.target.files ?? []));
      event.target.value = "";
    },
    [addFiles],
  );

  const openFilePicker = useCallback(() => fileInputRef.current?.click(), []);

  const focus = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.focus();
    const end = textarea.value.length;
    textarea.setSelectionRange?.(end, end);
  }, []);

  /** An example question goes into the box for the user to adjust; it is not sent. */
  const fill = useCallback(
    (value: string) => {
      setText(value);
      window.setTimeout(focus, 0);
    },
    [focus, setText],
  );

  const dropZoneProps = {
    onDragEnter: (event: DragEvent<HTMLElement>) => {
      if (!hasDraggedFiles(event) || disabled) return;
      event.preventDefault();
      dragDepthRef.current += 1;
      setIsDragging(true);
    },
    onDragOver: (event: DragEvent<HTMLElement>) => {
      if (!hasDraggedFiles(event) || disabled) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
    },
    onDragLeave: (event: DragEvent<HTMLElement>) => {
      if (!hasDraggedFiles(event)) return;
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
      if (dragDepthRef.current === 0) setIsDragging(false);
    },
    onDrop: (event: DragEvent<HTMLElement>) => {
      if (!hasDraggedFiles(event)) return;
      event.preventDefault();
      dragDepthRef.current = 0;
      setIsDragging(false);
      addFiles(Array.from(event.dataTransfer?.files ?? []));
    },
  };

  // Grow with the text; useLayoutEffect so the height is right before the paint. scrollHeight
  // leaves out the borders, which a border-box height includes; without them the box is 2px
  // short and shows a scrollbar for one line. It scrolls only once it reaches the cap.
  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    const borders = textarea.offsetHeight - textarea.clientHeight;
    const needed = textarea.scrollHeight + borders;
    textarea.style.height = `${Math.min(needed, MAX_TEXTAREA_HEIGHT_PX)}px`;
    textarea.style.overflowY = needed > MAX_TEXTAREA_HEIGHT_PX ? "auto" : "hidden";
  }, [text]);

  return {
    text,
    attachments,
    isDragging,
    isUploading,
    canSend,
    canAttach: !disabled && attachments.length < maxImages,
    charCount: text.length,
    maxChars,
    showCharCounter: text.length >= Math.floor(maxChars * AI_CHAR_COUNTER_THRESHOLD),
    isOverLimit,
    textareaRef,
    fileInputRef,
    dropZoneProps,
    handleChange,
    handleKeyDown,
    handlePaste,
    handleFileInputChange,
    openFilePicker,
    removeAttachment,
    submit,
    fill,
    focus,
  };
};

export type AiComposerState = ReturnType<typeof useAiComposer>;
